import {
	ArticleFragment,
	GetArticleByUrlDocument,
	GetArticleByUrlQuery,
	GetArticleByUrlQueryVariables,
	LinkedListFragment,
	LinkedTitleFragment,
} from '@/features/editorial/graphql/queries/GetArticleByUrl.query';
import { getArticleBackdrop, getLocalizedArticleImage } from '@/features/editorial/helper';
import { AssertByTypename, toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { toastError } from '@/helpers/vfm/toast-helper';

import { BackdropProfile, ImageFormat } from '@/@types/graphql-types';
import { timestamp } from '@/helpers/date-helper';
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

export interface Provision {
	author: ArticleFragment['author'];
	article: ArticleFragment['content'];
	mainObject: LinkedTitleFragment | LinkedListFragment | null;
	publishDate: string;
}

export interface ListProvision extends Provision {
	mainObject: LinkedListFragment;
}

export interface NewsProvision extends Provision {
	mainObject: LinkedTitleFragment;
}

export function useArticle(pageSize = 10) {
	const { language, country } = storeToRefs(useLanguageStore());
	const { editorial: editorialFilters } = storeToRefs(useFilterStore());
	const { isHideSeenDislikedTitlesEnabled } = storeToRefs(useUserStore());
	const { activeDomain } = storeToRefs(useRoutingStore());
	const { setArticleJsonLD } = useMetaStore();

	const { t } = useI18n();
	const route = useRoute();

	const queryVariables = computed<GetArticleByUrlQueryVariables>(() => {
		return {
			fullPath: route.path,
			site: activeDomain.value,
			country: country.value,
			language: language.value,
			first: pageSize,
			profile: BackdropProfile.S1440,
			format: ImageFormat.Png,
			filter: {
				...toGraphqlFilters(
					editorialFilters.value,
					editorialFilters.value.providers,
					isHideSeenDislikedTitlesEnabled.value
				),
				includeTitlesWithoutUrl: true,
			},
			withOffers: false,
		};
	});

	const enabled = computed(
		() => route.name === 'app.editorial.article' || route.name === 'app.editorial.subdomain.article'
	);

	const { result, fetchMore, loading, onError, onResult } = useQuery<GetArticleByUrlQuery>(
		GetArticleByUrlDocument,
		queryVariables,
		{
			errorPolicy: 'all',
			enabled,
		}
	);

	onError(error => {
		captureMessageForSentry('[GraphQL Get Article]:', { error: error, where: 'Component: useArticle' }, 'error');
		if (process.client) {
			showErrorToast();
		}
	});

	type ArticleNode = AssertByTypename<GetArticleByUrlQuery['urlV2']['node'], 'Article'>;
	const article = computed(() => result.value?.urlV2.node as ArticleNode);

	const articleContent = computed(() => article.value.content);
	const author = computed(() => article.value.author);

	onResult(({ data }) => {
		if (data != null) setArticleJsonLD(article.value, route.fullPath);
	});

	// isLoading only true before first data is loaded, not onLoadNext()
	const isLoading = computed(() => loading.value && !article.value);

	const mainObject = computed<Provision['mainObject']>(() => {
		const mainObject = article.value.mainObjects?.[0];
		if (mainObject == null) return null;

		type Node = typeof mainObject.node;
		return mainObject.node as AssertByTypename<Node, 'Movie' | 'Show' | 'GenericTitleList'>;
	});

	const publishDate = computed(() => {
		if (!article.value.content?.createdAt) return '';

		return timestamp(
			new Date(article.value.content.createdAt),
			{ year: 'numeric', month: 'long', day: 'numeric' },
			country.value,
			language.value
		).date;
	});

	const isUpdated = computed(() => {
		if (!article.value.content?.updatedAt) return false;

		const publishedDate = new Date(article.value.content.createdAt);
		const updatedDate = new Date(article.value.content.updatedAt);
		// isUpdated only true for different days
		return publishedDate.setHours(0, 0, 0, 0) !== updatedDate.setHours(0, 0, 0, 0);
	});

	const updatedDate = computed(() => {
		if (!article.value.content?.updatedAt) return '';

		return timestamp(
			new Date(article.value.content.updatedAt),
			{ year: 'numeric', month: 'long', day: 'numeric' },
			country.value,
			language.value
		).date;
	});

	const mainImageEntityBackdrop = computed(() => article.value?.content?.mainImageEntityBackdrop?.backdropUrl || '');

	const articleBackdropUrl = computed(() => {
		if (!article.value?.content?.images) return null;

		return getLocalizedArticleImage(article.value.content.images || [], 'backdrop', language.value);
	});

	const backdrop = computed(() => {
		if (!!articleBackdropUrl.value) return articleBackdropUrl.value;

		if (!!mainImageEntityBackdrop.value) return `https://www.justwatch.com/images${mainImageEntityBackdrop.value}`;

		return getArticleBackdrop(mainObject.value);
	});

	function showErrorToast() {
		toastError({ message: t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	// Load more function for pagination
	async function onLoadNext({ event }: { event: { target?: { complete: (len: number) => void } } }) {
		if (!event.target || mainObject.value?.__typename !== 'GenericTitleList') return;

		if (loading.value) return;

		const endCursor = mainObject.value.titles?.pageInfo?.endCursor;

		if (!endCursor) return;

		try {
			await fetchMore({
				variables: {
					after: endCursor,
				},
				updateQuery(
					previousResult: GetArticleByUrlQuery,
					{ fetchMoreResult }: { fetchMoreResult?: GetArticleByUrlQuery }
				) {
					if (!fetchMoreResult) return previousResult;

					const article = previousResult.urlV2.node as ArticleFragment;
					const mainObject = article.mainObjects[0].node as ListProvision['mainObject'];

					const fetchedTitles = (
						(fetchMoreResult.urlV2.node as ArticleFragment).mainObjects[0]
							.node as ListProvision['mainObject']
					).titles;

					if (mainObject.titles.edges == null || fetchedTitles.edges == null) return previousResult;

					mainObject.titles.edges.push(...fetchedTitles.edges);
					mainObject.titles.pageInfo = fetchedTitles.pageInfo;

					return previousResult;
				},
			});
		} catch (error: any) {
			captureMessageForSentry(
				'[GraphQL List Article Page Load More Titles Issue]:',
				{ error, where: 'Origin: editorial-article-provider, method: onLoadNext' },
				'error'
			);
		} finally {
			const edgeCount = ((mainObject.value as ListProvision['mainObject']).titles.edges ?? []).length;
			event.target?.complete(edgeCount);
		}
	}

	return {
		article: articleContent,
		author,
		backdrop,
		isUpdated,
		isLoading,
		listEvents: {
			'load-next': onLoadNext,
		},
		mainObject,
		publishDate,
		updatedDate,
	};
}
